/**
 * Presentational chrome for the v3 landing page: brand mark, top bar, chip
 * face, bottom-nav slots, feature-card train, trusted pill. All geometry and
 * tints come from the Landing Figma (qvKH4DGebnMCNK7SJ72iCm, nodes 2:3/6:1966).
 *
 * Every state change here is CONTINUOUS: components receive the page's scroll
 * progress `s` (scroll offset in viewport-heights, a MotionValue) and map their
 * transform/opacity from it via framer's computed `useTransform` — nothing is
 * triggered at thresholds, so slow scrolling holds any mid-transition frame.
 */
import {
  Avatar,
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownTrigger,
  LOGO_LOADER_FLIGHT_TARGET_ATTR,
} from '@flashka/shared/ui';

import {
  AnimatePresence,
  motion,
  type MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion';
import ISO6391 from 'iso-639-1';
import { ChevronDownIcon, MoonIcon, SmartphoneIcon, SunIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import {
  type ComponentProps,
  memo,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { SignUpButton, useAuth } from '@/auth/better/session';
import { authDialogs } from '@/auth/better/store';
import { useLocalStorage } from '@/components/hooks/use-local-storage';
import { StoreQrTiles } from '@/components/store-qr/store-qr-tiles';
import {
  FALLBACK_USER_COUNT,
  formatCompactMillions,
  formatExactCount,
} from '@/hooks/use-live-user-count';
import { loadLanguage } from '@/i18n';
import { clickSignupButtonDataLayer } from '@/lib/data-layer/utils';
import { cn } from '@/lib/utils';
import {
  featurePageForPath,
  type Lang,
  localizePath,
  rememberLangPick,
  type SectionPage,
  sectionPageForPath,
  splitLangFromPath,
  stripLangPrefix,
  SUPPORTED_LANGS,
} from '@/seo/marketing-routes';
import { useTheme } from '@/settings/theme';

import {
  DECK_RISE_SPAN,
  LANDING_FEATURES,
  LANDING_GROUPS,
  LANDING_SPANS,
  LANDING_TIMELINE,
  type LandingFeature,
} from './landing-features';
import { CARDS_WITH_KA, setKaInCard } from './landing-ka';
import { useStoreDevice, useTrackGetApp } from './landing-store';
import {
  FeatureMedia,
  type MediaPhase,
  preloadFeatureMedia,
} from './media/feature-media';

/** Same spring as the calendar-widget / study-group leaderboard morphs
 *  (`SPRING` in `calendar-widget.tsx`). Used for the one-shot intro pops —
 *  the scroll choreography itself is scrubbed, not sprung. */
export const LAYOUT_SPRING = { type: 'spring', stiffness: 300, damping: 30 } as const;

/**
 * The landing's glass, PULLED from the Figma landing file (top bar 38:1480,
 * cards 12:354, FAQ 38:1581) rather than borrowed from the DS components:
 * every chrome surface carries the "Drop shadow +Glass" effect style — a
 * DROP_SHADOW of #0000000F at (0, 2) blur 8 spread 0 (= the DS
 * `shadow-card-row`, "Shadow-Secondary") plus a GLASS effect of radius 10 —
 * over a 1px `secondary-200` border. What differs per surface is the fill:
 *
 *   pills / top bar   `brand/secondary/10`  = white at 10%   (`secondary-50/10`)
 *   feature cards     `brand/secondary/70`  = #fafafa at 70% (`secondary-50/70`)
 *   FAQ container     rgba(250,250,250,.9)  = #fafafa at 90% (`secondary-50/90`)
 *
 * The GLASS radius maps to `backdrop-filter: blur(10px)`; the frame's Liquid
 * Glass variables (opacity 60, light angle −45°, splay 6 — refraction and
 * light) have no CSS equivalent and are dropped, as the DS does everywhere.
 * Fills are `secondary-50` so they auto-invert in dark mode — no `dark:`
 * overrides. NOTE: never `shadow-card` on web — the shadcn `--card` color
 * variable hijacks it and paints the shadow white.
 */
const GLASS_EDGE = 'border border-secondary-200 backdrop-blur-[10px] shadow-card-row';
/** Pills, clusters, the top bar and the brand pill: white at 10%. */
export const GLASS_PILL_CLASS = `${GLASS_EDGE} bg-secondary-50/10`;
/** A sheet that holds its own content (the agent chat, Figma 119:8431):
 *  #fafafa at 90%, as the FAQ container. */
export const GLASS_SHEET_CLASS = `${GLASS_EDGE} bg-secondary-50/90`;
/**
 * The review cards over the world map (Figma 39:5723): `brand/secondary/30`
 * = white at 70%, the same border and drop — but this frame carries the
 * stronger `Glass` style, radius 27 (the DS Dialog / bottom-sheet frost), not
 * the chrome's 10. Pulled, not tuned: over a dotted map 10px leaves the dots
 * legible through the card; 27px turns them into a soft wash behind the text.
 */
export const GLASS_REVIEW_CLASS =
  'border border-secondary-200 bg-secondary-50/70 backdrop-blur-[27px] shadow-card-row';

/** Router state asking the landing to open on one feature card: set by the
 *  pages that share the landing's chrome (About), whose Study Tools menu and
 *  footer links point at cards that only the landing has. */
type LandingCardState = { lv3Card?: number };

/** Go to the landing, in the current language, opened on card `index`. */
export function useOpenLandingCard(): (index: number) => void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (index) => {
    const { lang } = splitLangFromPath(pathname);
    navigate(localizePath('/home', lang), {
      state: { lv3Card: index } satisfies LandingCardState,
    });
  };
}

/**
 * On the landing: once, if it was opened for a card (`useOpenLandingCard`),
 * `open(index)` jumps there — and the request is cleared, so a reload or a
 * trip back through history starts from the top again.
 *
 * A feature's own URL (`/home/quizzes`, `FEATURE_PAGES`) opens on its card
 * too, on every visit, and keeps its URL: it is the page search sends a
 * reader to for that feature.
 */
export function useLandingCardArrival(open: (index: number) => void) {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const card = (state as LandingCardState | null)?.lv3Card;
  const feature = featurePageForPath(splitLangFromPath(pathname).path);
  const urlCard = feature ? LANDING_FEATURES.findIndex((f) => f.id === feature.id) : -1;
  useEffect(() => {
    if (urlCard < 0) return;
    const frame = requestAnimationFrame(() => open(urlCard));
    return () => cancelAnimationFrame(frame);
  }, [urlCard, open]);
  useEffect(() => {
    if (typeof card !== 'number') return;
    // After the page has laid out its runway; `open` must be stable.
    const frame = requestAnimationFrame(() => {
      open(card);
      navigate(pathname, { replace: true, state: null });
    });
    return () => cancelAnimationFrame(frame);
  }, [card, open, navigate, pathname]);
}

/** Marks a landing section that has a URL of its own (`SECTION_PAGES`). */
export const sectionAnchor = (id: SectionPage['id']) => ({ 'data-lv3-section': id });

/** Scroll the landing to one of its sections; false when it is not on screen
 *  (another page, e.g. About, renders the footer that links to it). */
export function scrollToLandingSection(
  id: SectionPage['id'],
  behavior: ScrollBehavior = 'auto',
): boolean {
  const el = document.querySelector(`[data-lv3-section="${id}"]`);
  if (!el) return false;
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - sectionOffset(el),
    behavior,
  });
  return true;
}

/** Room for the phone's floating top bar, which would cover the section's
 *  opening line; the desktop block carries its own top padding. */
const sectionOffset = (el: Element) => (el.closest('.lv3-phone') ? 96 : 0);

/**
 * On the landing: a section's own URL (`/home/ai-agent`) opens scrolled to
 * that section, on every visit, and keeps its URL — the page search sends a
 * reader to for it.
 */
export function useLandingSectionArrival() {
  const { pathname } = useLocation();
  const section = sectionPageForPath(splitLangFromPath(pathname).path);
  useEffect(() => {
    if (!section) return;
    // The page grows for a while after mount (the runway, the blocks below
    // it), so keep re-aiming each frame until the section holds still at the
    // top — for up to ~3s, and never against a reader who starts scrolling.
    let frame = 0;
    let tries = 0;
    let settled = 0;
    let cancelled = false;
    const stop = () => {
      cancelled = true;
    };
    const userInput = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
    userInput.forEach((e) => window.addEventListener(e, stop, { passive: true }));
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-lv3-section="${section.id}"]`);
      if (el) {
        const top = el.getBoundingClientRect().top - sectionOffset(el);
        if (Math.abs(top) < 2) settled += 1;
        else {
          settled = 0;
          window.scrollTo({ top: top + window.scrollY });
        }
      }
      tries += 1;
      if (settled < 10 && tries < 180) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      userInput.forEach((e) => window.removeEventListener(e, stop));
    };
  }, [section]);
}

/** The desktop scene needs `lg` (1024px) and up; below it the pages fall
 *  back to their stacked phone layout. */
export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

/** The 36/44 two-tone section headline (Figma D2); the accent words take
 *  `text-primary-600`. */
export const HEADLINE_CLASS =
  'text-center text-[36px] font-bold leading-[44px] text-secondary-800';

/**
 * The marketing pages' dot lattice, painted ONCE on a fixed layer behind
 * everything (inside the page wrapper's `isolate` context). It used to be the wrapper's own
 * `background-attachment: fixed`, which Safari treats as a slow-repaint
 * object: the whole page then scrolls on the main thread and repaints the
 * viewport every frame, re-blurring every glass surface. A fixed box's
 * background area is the viewport, so the dots sit exactly where they did —
 * the world map's dot snapping and the review fades' `bg-fixed` dots still
 * land on them.
 */
export function PageLattice() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 bg-background bg-dotted-pattern bg-dotted-size dark:bg-dotted-pattern-dark"
    />
  );
}

/** Scroll plumbing shared with the chrome: `s` is the scroll offset in
 *  viewport-heights (0 = hero, 1 = first card centered, …); `layoutTick`
 *  bumps after any re-measure (resize/fonts) so computed values refresh. */
export type LandingScroll = {
  s: MotionValue<number>;
  layoutTick: MotionValue<number>;
  /** 0 → chrome shown; 1 → the reader is travelling (a fast scroll back UP),
   *  chrome away. See {@link useScrollTravel}. */
  travelling: MotionValue<number>;
};

// ── Fast-scroll gate ─────────────────────────────────────────────────────────
//
// A fast climb back UP the page is navigation, not reading — the visitor is
// heading for the top — so nothing should float along. Racing DOWN is still
// reading ahead and hides nothing (user decision 2026-09-24: the bar and Ka
// vanishing on a quick scroll down was an annoyance). Speed is the UPWARD pace
// in viewport heights per second — the page's own unit — smoothed over a short
// window so a single wheel notch (one frame's spike) never counts, and held for
// `sustainMs` before anything hides.
// The chrome returns once the speed has fallen under `showBelowVhPerS` for
// `idleMs`, or `idleMs` after the last scroll event, whichever comes first;
// the debounce is what stops a brief pause between two flicks flashing it in.

export const TRAVEL = {
  hideAboveVhPerS: 1.5,
  sustainMs: 100,
  showBelowVhPerS: 0.25,
  idleMs: 250,
  smoothingMs: 80,
} as const;
/** One spring for the out and the in, so both read as the same motion. */
const TRAVEL_SPRING = { stiffness: 260, damping: 30 } as const;

/** 0 while reading, 1 while travelling, sprung between the two. */
export function useScrollTravel(
  scrollY: MotionValue<number>,
  reduceMotion: boolean,
): MotionValue<number> {
  const target = useMotionValue(0);
  const travelling = useSpring(
    target,
    reduceMotion ? { stiffness: 10_000, damping: 500 } : TRAVEL_SPRING,
  );
  const st = useRef({
    lastY: 0,
    lastT: 0,
    /** Smoothed pace UP the page (negative while going down), vh/s. */
    upPace: 0,
    fastSince: null as number | null,
    stopTimer: undefined as number | undefined,
    slowTimer: undefined as number | undefined,
  });

  const show = () => {
    const c = st.current;
    window.clearTimeout(c.stopTimer);
    window.clearTimeout(c.slowTimer);
    c.stopTimer = c.slowTimer = undefined;
    c.fastSince = null;
    target.set(0);
  };

  useMotionValueEvent(scrollY, 'change', (y) => {
    const c = st.current;
    const now = performance.now();
    const dt = now - c.lastT;
    c.lastT = now;
    // A gap means a new gesture: rebase, and let the next event measure it.
    if (dt <= 0 || dt > 300) {
      c.lastY = y;
      c.upPace = 0;
      c.fastSince = null;
      return;
    }
    // Signed, positive going UP: scrollY falls as the page climbs.
    const upVhPerS = (c.lastY - y) / (dt / 1000) / (window.innerHeight || 1);
    c.lastY = y;
    c.upPace += (upVhPerS - c.upPace) * (1 - Math.exp(-dt / TRAVEL.smoothingMs));
    // Going down reads as zero: it never hides, and it brings the chrome back.
    const speed = Math.max(0, c.upPace);

    if (target.get() === 0) {
      if (speed >= TRAVEL.hideAboveVhPerS) {
        c.fastSince ??= now;
        if (now - c.fastSince >= TRAVEL.sustainMs) target.set(1);
      } else {
        c.fastSince = null;
      }
    }
    if (target.get() === 1) {
      // Every event re-arms the stop timer; the slow timer arms once the pace
      // drops and is NOT re-armed by slow events, so easing into reading speed
      // brings the chrome back without a full stop.
      window.clearTimeout(c.stopTimer);
      c.stopTimer = window.setTimeout(show, TRAVEL.idleMs);
      if (speed < TRAVEL.showBelowVhPerS) {
        c.slowTimer ??= window.setTimeout(show, TRAVEL.idleMs);
      } else {
        window.clearTimeout(c.slowTimer);
        c.slowTimer = undefined;
      }
    }
  });

  useEffect(() => {
    const c = st.current;
    return () => {
      window.clearTimeout(c.stopTimer);
      window.clearTimeout(c.slowTimer);
    };
  }, []);

  return travelling;
}

export const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Ka and its line step back as the deck rises over them (user 2026-09-24: no
 * figure left behind the first card). 1 = shown, 0 = gone; they are gone
 * within the first half of the rise, before the card's top edge reaches the
 * line, and return in step if the reader scrolls back up. Only the runway's
 * stretch is covered: from the hand-over on, Ka belongs to the page below.
 */
export function kaCoverAt(s: number, reduceMotion: boolean): number {
  const riseStart = LANDING_TIMELINE.cardThrowStart[0] - DECK_RISE_SPAN;
  if (s >= LANDING_TIMELINE.handover) return 1;
  // Reduced motion has no rise: Ka steps off where card 0 cuts in, never
  // leaving an empty stage in between.
  if (reduceMotion) return s >= LANDING_TIMELINE.cardThrowStart[0] ? 0 : 1;
  return 1 - clamp01((s - riseStart) / (DECK_RISE_SPAN * 0.5));
}
export const easeInOutCubic = (v: number) =>
  v < 0.5 ? 4 * v * v * v : 1 - Math.pow(-2 * v + 2, 3) / 2;

/**
 * The page background's dot grid, straight from the Tailwind tokens the app
 * uses everywhere (`bg-dotted-pattern` + `bg-dotted-size` in
 * `flashka-web/tailwind.config.js`): a `radial-gradient` circle of radius
 * 1.5px tiled every 10px. Because the background is `bg-fixed`, the tiling
 * origin is the VIEWPORT, and each tile centres its circle — so a background
 * dot sits at every viewport coordinate ≡ 5 (mod 10) on both axes.
 *
 * The world map derives its lit-dot positions from these two constants, and
 * snaps its SVG origin to the same lattice, so a lit dot lands exactly on a
 * background dot instead of near one.
 */
export const DOT_PITCH = 10;
export const DOT_RADIUS = 1.5;
/** Offset of a dot centre inside its tile (the radial-gradient's centre). */
export const DOT_CENTER = DOT_PITCH / 2;

// ── Brand ────────────────────────────────────────────────────────────────────

/** The square Flashka mark: dark tile + the two card bars (Figma node 2:4745). */
export function LogoMark({
  className,
  flightTarget = false,
}: {
  className?: string;
  /**
   * The boot loader's mark flies into THIS logo when the page first appears
   * (`releaseBootLogoLoader`): set it on the logo the visitor sees first.
   */
  flightTarget?: boolean;
}) {
  return (
    // Two marks, one per theme, from the designer (2026-09-23). LIGHT: the
    // brand's two-tone mark (Figma 48:20944) — a secondary-700 tile holding
    // the yellow and violet bars; those two hues are the logo's own, not DS
    // tokens, hence the literal hex. DARK: the single-colour mark (Logo.svg)
    // in #E5E5E5 — secondary-800 inverts to exactly that. The `dark` class on
    // <html> switches them.
    <span
      aria-hidden
      className={cn('relative block', className)}
      {...(flightTarget ? { [LOGO_LOADER_FLIGHT_TARGET_ATTR]: '' } : {})}>
      <span className="flex h-full w-full flex-col items-stretch gap-[6.5%] rounded-[16%] bg-secondary-700 py-[8.6%] pl-[17.4%] pr-[9.3%] dark:hidden">
        <span className="min-h-px flex-1 rounded-[9.7%] bg-[#fcd34d]" />
        <span className="min-h-px flex-1 rounded-[9.7%] bg-[#a78bfa]" />
      </span>
      <svg
        viewBox="0 0 386 418"
        fill="currentColor"
        className="hidden h-full w-full text-secondary-800 dark:block">
        <path d="M323.406 0C357.976 0.000175125 386.001 28.0247 386.001 62.5947V354.703C386.001 389.273 357.976 417.298 323.406 417.298H62.5947C28.0246 417.298 0 389.273 0 354.703V62.5947C0 28.0246 28.0246 0 62.5947 0H323.406ZM104.03 220.591C83.3313 220.591 66.5518 237.371 66.5518 258.07V343.345C66.5518 364.044 83.3321 380.824 104.031 380.824H313.431C334.13 380.824 350.909 364.044 350.909 343.345V258.07C350.909 237.371 334.13 220.591 313.431 220.591H104.03ZM104.03 33.2754C83.3313 33.2755 66.5518 50.0558 66.5518 70.7549V156.03C66.552 176.729 83.3314 193.509 104.03 193.509H313.43C334.129 193.509 350.909 176.729 350.909 156.03V70.7549C350.909 50.0557 334.129 33.2754 313.43 33.2754H104.03Z" />
      </svg>
    </span>
  );
}

/** "flashka" wordmark (Figma node 2:4748), tinted via `currentColor`. */
export function FlashkaWordmark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 95.41 21.74" fill="currentColor" aria-hidden className={className}>
      <path d="M86.3534 21.7097C82.5877 21.7097 80.0576 18.9148 80.0576 13.6194C80.0576 8.29446 82.5877 5.49962 86.3534 5.49962C88.8834 5.49962 90.7663 6.76465 91.5312 8.67691H91.7371L91.8342 6.77347C91.8622 6.22453 92.3155 5.79381 92.8651 5.79381H94.3823C94.9524 5.79381 95.4145 6.25597 95.4145 6.82607V20.4127C95.4145 20.9828 94.9524 21.4449 94.3823 21.4449H92.7694C92.1993 21.4449 91.7371 20.9828 91.7371 20.4127V18.5324H91.5312C90.7663 20.4447 88.8834 21.7097 86.3534 21.7097ZM87.7361 18.7089C90.3544 18.7089 91.6783 16.6201 91.6783 14.4137V12.7956C91.6783 10.5597 90.3544 8.50039 87.7361 8.50039C85.3825 8.50039 83.7939 10.1773 83.7939 13.6194C83.7939 17.032 85.3825 18.7089 87.7361 18.7089Z" />
      <path d="M67.1866 21.4467C66.6165 21.4467 66.1544 20.9846 66.1544 20.4145V1.03227C66.1544 0.462165 66.6165 6.53948e-06 67.1866 6.53948e-06H68.8584C69.4285 6.53948e-06 69.8906 0.462164 69.8906 1.03226V12.768L71.2145 11.2676L76.1463 6.11418C76.341 5.9107 76.6104 5.79562 76.8921 5.79562H78.5443C79.4604 5.79562 79.9225 6.90009 79.2795 7.5525L74.9508 11.9443L80.1151 19.8499C80.5637 20.5365 80.071 21.4467 79.2509 21.4467H77.3944C77.0447 21.4467 76.7187 21.2696 76.5284 20.9762L73.3033 16.0041L72.3619 14.3861L69.8906 16.8279V20.4145C69.8906 20.9846 69.4285 21.4467 68.8584 21.4467H67.1866Z" />
      <path d="M50.2978 21.4467C49.7277 21.4467 49.2656 20.9846 49.2656 20.4145V1.03227C49.2656 0.462165 49.7277 6.53948e-06 50.2978 6.53948e-06H51.9107C52.4808 6.53948e-06 52.943 0.462164 52.943 1.03226V8.76698H53.1195C54.0021 6.61936 56.0026 5.50143 58.2679 5.50143C61.4452 5.50143 63.8281 7.32543 63.8281 12.0619V20.4145C63.8281 20.9846 63.366 21.4467 62.7959 21.4467H61.183C60.6129 21.4467 60.1507 20.9846 60.1507 20.4145V12.8563C60.1507 9.91433 59.1505 8.53162 56.8558 8.53162C54.3845 8.53162 52.943 10.4733 52.943 12.8268V20.4145C52.943 20.9846 52.4808 21.4467 51.9107 21.4467H50.2978Z" />
      <path d="M41.0437 21.7391C37.3349 21.7391 34.9014 20.0927 34.4226 17.5205C34.319 16.9637 34.7924 16.5025 35.3588 16.5025H36.9392C37.5027 16.5025 37.9316 16.9706 38.1296 17.4982C38.541 18.5943 39.6487 19.0914 41.1614 19.0914C43.0148 19.0914 43.9857 18.3853 43.9857 17.1497C43.9857 15.7081 43.0737 15.3551 40.6613 15.0021C36.6603 14.4137 34.748 13.3546 34.748 10.3538C34.748 7.4413 37.3075 5.49962 41.1614 5.49962C44.4703 5.49962 46.757 6.99877 47.303 9.39831C47.4313 9.96192 46.9531 10.4421 46.3751 10.4421H44.8228C44.26 10.4421 43.836 9.97218 43.624 9.45084C43.2502 8.53147 42.334 8.11794 41.0732 8.11794C39.3963 8.11794 38.4254 8.82401 38.4254 10.0302C38.4254 11.3541 39.4845 11.7659 42.1028 12.119C45.8391 12.6191 47.7514 13.737 47.7514 16.8261C47.7514 19.9739 45.0742 21.7391 41.0437 21.7391Z" />
      <path d="M23.8036 21.7097C20.0379 21.7097 17.5079 18.9148 17.5079 13.6194C17.5079 8.29446 20.0379 5.49962 23.8036 5.49962C26.3337 5.49962 28.2165 6.76465 28.9814 8.67691H29.1873L29.2845 6.77347C29.3125 6.22453 29.7657 5.79381 30.3154 5.79381H31.8325C32.4026 5.79381 32.8648 6.25597 32.8648 6.82607V20.4127C32.8648 20.9828 32.4026 21.4449 31.8325 21.4449H30.2196C29.6495 21.4449 29.1873 20.9828 29.1873 20.4127V18.5324H28.9814C28.2165 20.4447 26.3337 21.7097 23.8036 21.7097ZM25.1863 18.7089C27.8046 18.7089 29.1285 16.6201 29.1285 14.4137V12.7956C29.1285 10.5597 27.8046 8.50039 25.1863 8.50039C22.8328 8.50039 21.2441 10.1773 21.2441 13.6194C21.2441 17.032 22.8328 18.7089 25.1863 18.7089Z" />
      <path d="M2.70658 8.76697V20.4145C2.70658 20.9846 3.16874 21.4467 3.73884 21.4467H5.35174C5.92184 21.4467 6.384 20.9846 6.384 20.4145V8.76697H10.9022V5.79561H6.384V4.00103C6.384 3.11845 6.73703 2.50064 8.03149 2.50064H8.97032C9.54042 2.50064 10.0026 2.03849 10.0026 1.46839V1.03226C10.0026 0.462158 9.54042 0 8.97032 0H7.00181C4.29523 0 2.70658 1.32387 2.70658 3.7951V5.79561H1.03226C0.462158 5.79561 0 6.25777 0 6.82787V7.73471C0 8.30481 0.462157 8.76697 1.03226 8.76697H2.70658ZM12.3267 8.76697V20.4145C12.3267 20.9846 12.7889 21.4467 13.359 21.4467H14.9719C15.542 21.4467 16.0041 20.9846 16.0041 20.4145V1.03226C16.0041 0.462158 15.542 0 14.9719 0H13.359C12.7889 0 12.3267 0.462158 12.3267 1.03226V5.79561V8.76697Z" />
    </svg>
  );
}

// ── Shared CTA wiring ────────────────────────────────────────────────────────

/**
 * Sign-up CTA with the same wiring as the old landing CTA (`LandingPage.tsx`):
 * Clerk modal when signed out, straight to the dashboard when signed in, with
 * the same PostHog + dataLayer capture.
 */
export function GetStartedCta({
  placement,
  size,
  variant = 'primary',
  fullWidth,
  leftIcon,
  children,
}: {
  /** Which of the page's Get Started buttons this is, on the click event —
   *  the same event fires from all of them. */
  placement: 'topBar' | 'phoneBar' | 'hero' | 'final' | 'floating';
  size: 's' | 'm' | 'xxl';
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
  leftIcon?: ComponentProps<typeof Button>['leftIcon'];
  children: string;
}) {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  const capture = () => {
    posthog.capture('Homepage-Get-Started-Clicked', {
      pathname: window.location.pathname,
      placement,
    });
    clickSignupButtonDataLayer();
  };

  const button = (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      leftIcon={leftIcon}
      onPress={() => {
        capture();
        if (isSignedIn) navigate('/dashboard');
      }}>
      {children}
    </Button>
  );

  if (isSignedIn) return button;
  return (
    <SignUpButton mode="modal" fallbackRedirectUrl="/">
      {button}
    </SignUpButton>
  );
}

/**
 * "Try it now" on every feature card (user 2026-09-24): a tertiary button to
 * sign in. The app has no separate login route — its sign-in is the unified
 * sign-in / sign-up card, which the landing opens as a modal (as Get Started
 * does); a visitor already signed in goes straight to the dashboard.
 */
export function TryItNowCta({
  featureId,
  size = 'l',
}: {
  featureId: string;
  /** `s` on the phone's pile, where it shares the badge's row. */
  size?: 's' | 'l';
}) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  return (
    <Button
      variant="tertiary"
      size={size}
      // No frost of its own: it sits on the card's glass, which already
      // blurs what is behind; a second backdrop pass per card cost frames.
      // (`glass` only drops the separate glass layer; the variant's own
      // web blur needs the class.)
      glass={false}
      className="web:backdrop-filter-none"
      onPress={() => {
        posthog.capture('Homepage-Try-It-Now-Clicked', {
          pathname: window.location.pathname,
          feature: featureId,
        });
        // Opened from the press itself, not a wrapping SignInButton: one Tab
        // stop, native Enter/Space, and the event captured for every activation.
        if (isSignedIn) navigate('/dashboard');
        else authDialogs.openSignIn();
      }}>
      {t('landingV3.features.tryItNow')}
    </Button>
  );
}

// ── Locale switcher (Figma node 11:56) ──────────────────────────────────────

/** Long language names keyed by code — the shape `useGetUserLanguage` /
 *  `LangBoundary` persist and read (see `language-provider`). */
const LANG_LONG_NAME: Record<Lang, string> = {
  en: 'English',
  it: 'Italian',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  sv: 'Swedish',
  pl: 'Polish',
  he: 'Hebrew',
  ca: 'Catalan',
};

/**
 * Locale dropdown for the landing top bar: the shared DS `Select` with a
 * ghost-Button trigger (Figma 11:56 — code label + chevron, 40px tall).
 * Lists every language in `SUPPORTED_LANGS`, switches live by navigating to
 * the localized marketing path (`LangBoundary` applies i18n + `<html lang>`
 * synchronously), and persists the pick in the same `user-language` key the
 * rest of the app reads — so a return visit to `/` lands localized.
 */
/**
 * Language switching for the landing. Three-part persistence, because the
 * landing lives at BOTH `/` (outside `LangBoundary`) and `/<lang>/home`
 * (inside it): i18next itself (caches `i18nextLng`, what a reload of the bare
 * `/` reads back); the app's `user-language` key, which `LangBoundary`
 * consults for a returning visitor; and the URL, the source of truth for the
 * indexable marketing routes.
 */
function useLocaleSwitch() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [, setStoredLanguage] = useLocalStorage<string>('user-language', '');

  const current = (SUPPORTED_LANGS.find(
    (l) => l === (i18n.language || 'en').split('-')[0],
  ) ?? 'en') as Lang;

  const options = SUPPORTED_LANGS.map((lang) => ({
    value: lang as Lang,
    // Native name reads better in a language picker than the English one.
    label: ISO6391.getNativeName(lang) || ISO6391.getName(lang) || lang,
  }));

  const setLang = (lang: Lang) => {
    // Its strings first (each language is its own chunk): switched before
    // they arrived, the page would sit blank behind LangBoundary meanwhile.
    void loadLanguage(lang).then(() => {
      i18n.changeLanguage(lang);
      setStoredLanguage(LANG_LONG_NAME[lang]);
      rememberLangPick(lang);
      navigate(localizePath(stripLangPrefix(pathname), lang) + search);
    });
  };

  return { current, options, setLang };
}

/** Sun/moon icon button (Figma node 7:2242) driving the app theme. */
export function ThemeToggleButton({ size = 'm' }: { size?: 's' | 'm' }) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const posthog = usePostHog();
  const dark = theme === 'dark';
  return (
    <Button
      variant="ghost"
      size={size}
      icon={dark ? SunIcon : MoonIcon}
      accessibilityLabel={t('landingV3.nav.themeToggle')}
      onPress={() => {
        posthog.capture(dark ? 'Dark-Mode-Disable' : 'Dark-Mode-Enable');
        setTheme(dark ? 'light' : 'dark');
      }}
    />
  );
}

/** The theme switch as a menu row: the phone's bar has no room for the
 *  button (Figma 116:972), so it lives in the Study Tools menu there. */
export function ThemeMenuItem() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const posthog = usePostHog();
  const dark = theme === 'dark';
  return (
    <DropdownItem
      icon={dark ? SunIcon : MoonIcon}
      onSelect={() => {
        posthog.capture(dark ? 'Dark-Mode-Disable' : 'Dark-Mode-Enable');
        setTheme(dark ? 'light' : 'dark');
      }}>
      {t('landingV3.nav.themeToggle')}
    </DropdownItem>
  );
}

// ── Chip face ────────────────────────────────────────────────────────────────

/** Base (hero) chip size; the floating group scales it down to
 *  `RAIL_CHIP_SIZE` as it docks, so border/radius shrink proportionally. */
export const HERO_CHIP_SIZE = 64;
/** A chip docked in the feature rail: 40px, a size down from the frame's 48
 *  (82:23718) — user: "make the rail smaller". The rail's slots are this size
 *  and the floating chips scale to it, so the two stay in step. */
export const RAIL_CHIP_SIZE = 40;

/**
 * The visual chip square. Purely presentational — position, rotation, drift
 * and docking all live on the floating group that wraps it (one shared
 * transform for icon + badge, so they can never desync).
 */
export function ChipFace({
  feature,
  onClick,
  onHoverChange,
  label,
}: {
  feature: LandingFeature;
  onClick: () => void;
  onHoverChange: (hovered: boolean) => void;
  label: string;
}) {
  const Icon = feature.icon;
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.06 }}
      onClick={onClick}
      onHoverStart={() => onHoverChange(true)}
      onHoverEnd={() => onHoverChange(false)}
      aria-label={label}
      style={{
        width: HERO_CHIP_SIZE,
        height: HERO_CHIP_SIZE,
        borderRadius: HERO_CHIP_SIZE * 0.318,
      }}
      className={cn(
        'flex cursor-pointer items-center justify-center border-[1.5px]',
        feature.chipClass,
        feature.shadowClass,
      )}>
      <Icon className={cn('h-[55%] w-[55%]', feature.iconClass)} />
    </motion.button>
  );
}

// ── The deck's box ─────────────────────────────────────────────────────────

/**
 * Where the TOP card sits — the deck and the rail both position from this one
 * box, so they cannot drift apart.
 *
 * - Horizontally the card is centred on the page, on the same line as the top
 *   bar; the rail hangs off its left edge, outside the centring. 998 wide
 *   (Figma 82:23781). A narrower desktop gives up card width rather than the
 *   rail's footing: 24px to the window + the 54px rail + a 16px gap is 94px,
 *   kept on BOTH sides so the card stays centred.
 * - Vertically it is centred in the room under the top bar: the bar ends
 *   106px down (top 40 + 66 tall with its hairline), so 93px of air above the
 *   card and 93px below it. The card is as tall as before (the viewport less
 *   292px); the 168px the old bottom bar used to reserve is shared out instead.
 */
const DECK_BOX_CLASS =
  'absolute bottom-[93px] left-1/2 top-[199px] w-[min(998px,calc(100%-188px))] -translate-x-1/2';

// ── Feature rail (Figma 82:23718) ───────────────────────────────────────────

/**
 * The feature rail (Figma 82:23718, a size down): the three groups as glass
 * pills stacked down the LEFT of the deck — Create (5), Plan (1), Study (4) —
 * 12px apart, each a 6px-padded pill of 40px chip slots 6px apart (the frame's
 * 48 / 8 / 16, scaled to the smaller chip). The slots
 * (`data-lv3-slot`) are invisible: the hero's floating chips land exactly on
 * them as the gather completes, and dim to 30% except the one whose card is
 * on top. Fades with scroll; 16px off the top card's left edge and centred
 * on it vertically, by static layout only — the slots are measured, so the
 * rail never animates its position.
 */
function FeatureRailInner({ scroll }: { scroll: LandingScroll }) {
  const { t } = useTranslation();
  const railOpacity = useTransform(() => {
    const s = scroll.s.get();
    const enter = clamp01((clamp01(s / LANDING_SPANS.hero) - 0.25) / 0.5);
    // Only relevant while the deck is on screen — fades as the stage
    // scrolls away with the last card.
    const exit = clamp01((s - LANDING_TIMELINE.runwayEnd) / 0.45);
    // The rail stays through a fast scroll (only the top bar steps aside).
    return enter * (1 - exit);
  });
  return (
    <nav
      aria-label={t('landingV3.nav.featuresNav')}
      className={cn('pointer-events-none z-10', DECK_BOX_CLASS)}>
      <motion.div
        style={{ opacity: railOpacity }}
        className="absolute right-[calc(100%+16px)] top-1/2 flex -translate-y-1/2 flex-col items-center gap-3">
        {LANDING_GROUPS.map((group) => (
          <div
            key={group.id}
            className={cn(
              'flex flex-col items-center gap-1.5 rounded-[20px] p-1.5',
              GLASS_PILL_CLASS,
            )}>
            {group.features.map((f) => (
              <div
                key={f.id}
                data-lv3-slot={f.id}
                style={{ width: RAIL_CHIP_SIZE, height: RAIL_CHIP_SIZE }}
              />
            ))}
          </div>
        ))}
      </motion.div>
    </nav>
  );
}

// ── Top bar (Figma node 6:1968) ──────────────────────────────────────────────

/**
 * Always mounted; slides down + fades in as the brand pill hands over
 * (both mapped from the same scroll progress, so the exchange is continuous).
 */
/**
 * "Study Tools ⌄" (Figma 64:23524): every feature as a menu row wearing the
 * same chip the feature rail gives it; choosing one lands on that card in the
 * deck. The phone's bar (landing-phone-bar) opens the same menu from a
 * smaller trigger, and folds About Us into it when the bar has no room.
 */
export function StudyToolsMenu({
  onJumpTo,
  size = 'm',
  label,
  icon,
  footer,
}: {
  onJumpTo: (index: number) => void;
  size?: 's' | 'm';
  /** The trigger's label; the desktop's "Study Tools" by default. */
  label?: string;
  /** Icon-only trigger (the label becomes its accessible name). */
  icon?: ComponentProps<typeof Button>['icon'];
  /** Rows after the features, under a separator. */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const text = label ?? t('landingV3.nav.studyTools');
  return (
    // `l`: 48px rows — two sizes up from the compact menu, room for the chips.
    <Dropdown size="l">
      <DropdownTrigger>
        {({ onPress }) =>
          icon ? (
            <Button
              variant="ghost"
              size={size}
              icon={icon}
              accessibilityLabel={text}
              onPress={onPress}
            />
          ) : (
            <Button
              variant="ghost"
              size={size}
              rightIcon={ChevronDownIcon}
              onPress={onPress}>
              {text}
            </Button>
          )
        }
      </DropdownTrigger>
      <DropdownContent align="start" sideOffset={12} minWidth={size === 's' ? 280 : 320}>
        {LANDING_FEATURES.map((feature, i) => (
          <DropdownItem key={feature.id} onSelect={() => onJumpTo(i)}>
            <span className="flex items-center gap-3">
              {/* The rail's chip for this feature, at row size. */}
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border',
                  feature.chipClass,
                )}>
                <feature.icon className={cn('h-[18px] w-[18px]', feature.iconClass)} />
              </span>
              {t(feature.badgeKey)}
            </span>
          </DropdownItem>
        ))}
        {footer && (
          <>
            <DropdownSeparator />
            {footer}
          </>
        )}
      </DropdownContent>
    </Dropdown>
  );
}

/**
 * The smartphone icon button (Figma 64:23530): the two stores as QR tiles a
 * reader scans from their desk, each also a link (`StoreQrTiles`, shared with
 * the in-app download dialog).
 */
function GetAppMenu() {
  const { t } = useTranslation();
  const trackGetApp = useTrackGetApp();
  return (
    <Dropdown size="l">
      <DropdownTrigger>
        {({ onPress }) => (
          <Button
            variant="ghost"
            size="m"
            icon={SmartphoneIcon}
            accessibilityLabel={t('landingV3.nav.getApp')}
            onPress={onPress}
          />
        )}
      </DropdownTrigger>
      <DropdownContent
        // Centred under the smartphone button, caption first — the panel
        // reads as that button's own.
        align="center"
        sideOffset={12}
        minWidth={382}
        topSlot={
          <div className="flex flex-col gap-3 p-3">
            <p className="text-center text-[14px] font-semibold leading-5 text-fg-subtle">
              {t('landingV3.nav.scanToGet')}
            </p>
            <StoreQrTiles onOpen={(store) => trackGetApp(store, 'top-bar')} />
          </div>
        }
      />
    </Dropdown>
  );
}

/** The flag shown for each landing language — the LANGUAGE, so Hebrew wears
 *  Israel's flag but is named עברית, and English the UK's (the frame's). National
 *  flags are public domain; files in `public/assets/landing/flags/`. */
const flagSrc = (lang: Lang) => `/assets/landing/flags/${lang}.svg`;

function FlagGlyph({ lang, className }: { lang: Lang; className?: string }) {
  return (
    // Figma 80:23569: 22px, radius 6, a secondary-200 hairline; the flag
    // fills the square (object-cover) whatever its own ratio.
    <span
      aria-hidden
      className={cn(
        'block h-[22px] w-[22px] shrink-0 overflow-clip rounded-[6px] border border-secondary-200',
        className,
      )}>
      <img src={flagSrc(lang)} alt="" className="h-full w-full object-cover" />
    </span>
  );
}

/**
 * The language switcher (Figma 80:23568): the current language's flag with a
 * chevron; a menu of every language as flag + native name.
 */
export function LanguageMenu() {
  const { t } = useTranslation();
  const { current, options, setLang } = useLocaleSwitch();
  return (
    <Dropdown size="l">
      <DropdownTrigger>
        {({ onPress, open }) => (
          <button
            type="button"
            onClick={onPress}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={t('landingV3.nav.language')}
            className="flex cursor-pointer items-center gap-1 text-fg-subtle transition-opacity hover:opacity-70">
            <FlagGlyph lang={current} />
            <ChevronDownIcon className="h-[22px] w-[22px]" />
          </button>
        )}
      </DropdownTrigger>
      <DropdownContent align="end" sideOffset={12} minWidth={220}>
        {options.map((o) => (
          <DropdownItem
            key={o.value}
            selected={o.value === current}
            onSelect={() => setLang(o.value)}>
            <span className="flex items-center gap-3">
              <FlagGlyph lang={o.value} />
              {o.label}
            </span>
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  );
}

/**
 * The fixed top bar (Figma 64:23518, 66px): logo · Study Tools ⌄ + About Us ·
 * theme, get-the-app, Get Started, the language flag. One glass pill, 66px
 * tall, 12/24/12 padding, 52px between the three groups. Get Started is
 * primary until the closing block's own CTA takes over.
 */
/** To About Us, in the current language, from the top of the page. */
export function useGoToAbout(): () => void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return () => {
    window.scrollTo(0, 0);
    navigate(localizePath('/home/about', splitLangFromPath(pathname).lang));
  };
}

/** About Us, a DS ghost button like the Study Tools trigger beside it (user
 *  2026-09-25). */
export function AboutUsButton({ size = 'm' }: { size?: 's' | 'm' }) {
  const { t } = useTranslation();
  const goToAbout = useGoToAbout();
  return (
    <Button variant="ghost" size={size} onPress={goToAbout}>
      {t('landingV3.nav.aboutUs')}
    </Button>
  );
}

export function TopBar({
  scroll,
  interactive,
  final = false,
  onJumpTo,
  onLogoClick,
  logoLabel,
}: {
  scroll: LandingScroll;
  interactive: boolean;
  /** The closing block ("Start Studying Now") is on: Get Started steps back to
   *  the secondary style so the page has one primary call at a time. */
  final?: boolean;
  /** Land on card `index` in the deck (the Study Tools menu). */
  onJumpTo: (index: number) => void;
  onLogoClick: () => void;
  /** What the mark does, for screen readers: back to the top on the landing,
   *  back to the landing elsewhere. */
  logoLabel?: string;
}) {
  const { t } = useTranslation();
  // A phone or tablet: the floating store button is the page's primary, and
  // QR codes to scan are pointless on the device itself (user 2026-09-28).
  const storeDevice = useStoreDevice();
  // Fractions of the HERO span, so the bar keeps pace with the chip gather.
  const reveal = useTransform(() =>
    clamp01((clamp01(scroll.s.get() / LANDING_SPANS.hero) - 0.35) / 0.4),
  );
  // Rises 24px in with the hero's reveal, then stays through every scroll: it
  // carries the page's Get Started, which is never out of reach (user
  // 2026-10-02 — it used to lift away while the reader raced back up).
  const y = useTransform(() => -24 * (1 - reveal.get()));
  const opacity = reveal;
  return (
    // `fixed`: the bar outlives the sticky stage and stays over the
    // normal-flow sections below the runway. The full-width row never takes
    // the pointer — only the pill does — or a 66px band across the window
    // would swallow clicks on the FAQ, tiles and footer beside the pill.
    <div className="pointer-events-none fixed inset-x-0 top-10 z-20 flex justify-center">
      <motion.div
        style={{ opacity, y, borderRadius: 24 }}
        className={cn(
          'flex items-center gap-[52px] py-3 pl-6 pr-3',
          GLASS_PILL_CLASS,
          interactive && 'pointer-events-auto',
        )}>
        <button
          type="button"
          onClick={onLogoClick}
          aria-label={logoLabel ?? t('landingV3.nav.backToTop')}
          className="cursor-pointer">
          <LogoMark className="h-[22px] w-[20.3px]" />
        </button>
        <div className="flex items-center gap-6">
          <StudyToolsMenu onJumpTo={onJumpTo} />
          <AboutUsButton />
        </div>
        {/* Right cluster (Figma 64:23528): theme · get-the-app · Get Started,
            4px apart, then the language flag 12px on. */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <ThemeToggleButton />
            {!storeDevice && <GetAppMenu />}
            <GetStartedCta
              placement="topBar"
              size="m"
              variant={final || storeDevice ? 'secondary' : 'primary'}>
              {t('landingV3.nav.getStarted')}
            </GetStartedCta>
          </div>
          <LanguageMenu />
        </div>
      </motion.div>
    </div>
  );
}

// ── Feature card deck (Figma nodes 11:50 / 12:242) ──────────────────────────

/**
 * Each cluster's cards sit as a stacked deck (under-cards peek 8px above the
 * top card, 16px narrower per level — Figma 11:50). The deck rises from the
 * bottom edge during the tail of the preceding Ka interstitial (Figma
 * 12:242), then each step of scroll throws the top card away to the right
 * with a little rotation, revealing the next. All
 * transforms are pure functions of scroll — slow scrolling holds a card
 * mid-throw.
 */
function FeatureCardDeckInner({
  scroll,
  reduceMotion,
}: {
  scroll: LandingScroll;
  reduceMotion: boolean;
}) {
  // The first two cards' media chunks are fetched while the reader is still on
  // the hero — idle time, off the critical path. Later cards prefetch as the
  // deck advances (see DeckCard).
  useEffect(() => {
    // Not on a metered or slow line: the two cards' editors are ~1 MB, and
    // they still load as the deck advances (DeckCard) for whoever scrolls.
    const connection = (
      navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string };
      }
    ).connection;
    if (connection?.saveData || /(^|-)(2g|3g)$/.test(connection?.effectiveType ?? ''))
      return;
    const first = LANDING_GROUPS[0].features.slice(0, 2);
    const warm = () => first.forEach((f) => preloadFeatureMedia(f.id));
    let idleId: number | undefined;
    let timer: number | undefined;
    // Only once the page has fully loaded — an idle slot before `load` would
    // have these forty-odd chunks competing with the hero's own assets.
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(warm, { timeout: 4000 });
      } else {
        timer = window.setTimeout(warm, 1500);
      }
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
      window.removeEventListener('load', schedule);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  return (
    <div className={cn('pointer-events-none z-[5]', DECK_BOX_CLASS)}>
      {/* ONE deck: Create, Plan and Study cards are a single pile, thrown in
          order — the groups live on as the rail's three pills. */}
      {LANDING_FEATURES.map((feature, g) => (
        <DeckCard
          key={feature.id}
          feature={feature}
          globalIndex={g}
          localIndex={g}
          scroll={scroll}
          reduceMotion={reduceMotion}
        />
      ))}
    </div>
  );
}

/**
 * A touch screen (an iPad in landscape gets this scene): the demos run `still`
 * there, as on the phone layout — a drag on the mind map or the occlusion
 * editor would pan or draw instead of moving the deck on.
 */
const TOUCH_SCREEN =
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: none) and (pointer: coarse)').matches;

function DeckCard({
  feature,
  globalIndex,
  localIndex,
  scroll,
  reduceMotion,
}: {
  feature: LandingFeature;
  globalIndex: number;
  localIndex: number;
  scroll: LandingScroll;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  const CARD = LANDING_SPANS.card;
  const throwStart = LANDING_TIMELINE.cardThrowStart[globalIndex];
  const clusterStart = throwStart - localIndex * CARD;

  // Deck rises from below the viewport during the closing stretch of the
  // preceding Ka interstitial (its top peeks in first, per Figma 12:242).
  const RISE_SPAN = DECK_RISE_SPAN;
  const rise = () =>
    reduceMotion
      ? scroll.s.get() >= clusterStart - RISE_SPAN
        ? 1
        : 0
      : easeInOutCubic(
          clamp01((scroll.s.get() - (clusterStart - RISE_SPAN)) / RISE_SPAN),
        );
  // Throw progress for THIS card over its (short) card span, on a crisper
  // ease-in than before: the card holds still while you read it, then leaves
  // decisively. Scroll-linked on purpose — the throw belongs to the gesture.
  // The last card is never thrown: it scrolls away with the stage.
  const isLast = globalIndex === LANDING_FEATURES.length - 1;
  const throwP = () => (isLast ? 0 : clamp01((scroll.s.get() - throwStart) / CARD));
  const thrown = () => Math.pow(throwP(), 1.9);
  // Continuous depth below the current top of the pile: `localIndex` while
  // the deck waits, shrinking by one for every card thrown ahead of it.
  const depth = () =>
    Math.max(0, localIndex - Math.max(0, (scroll.s.get() - clusterStart) / CARD));

  // What the media slot holds, decided by DECK position (every card is "in
  // view" on the sticky stage, so the viewport says nothing). The front card
  // is live while it still reads as at rest — the throw's first 15% moves it
  // under 25px — the card beneath is warm, the rest unmounted. See
  // media/feature-media.tsx for what each phase mounts.
  // A cluster's deck rises during the Ka interstitial before it; its top
  // card warms a beat before the rise (the reader is on Ka's line, not
  // scrolling) so the mount never lands inside the rise animation.
  const WARM_LEAD = 0.35;
  // Under reduced motion nothing is thrown: card g IS the deck for exactly its
  // span (the last one until the runway ends), and is live for all of it.
  const reducedEnd = isLast ? LANDING_TIMELINE.runwayEnd : throwStart + CARD;
  const phaseNow = (): MediaPhase => {
    const s = scroll.s.get();
    if (reduceMotion) {
      if (s >= throwStart && s < reducedEnd) return 'live';
      if (s >= throwStart - CARD && s < throwStart) return 'warm';
      return s < throwStart ? 'idle' : 'gone';
    }
    if (s < clusterStart - RISE_SPAN - WARM_LEAD) return 'idle';
    const d = depth();
    const tp = throwP();
    // Unmounted only once the card is invisible (its opacity reaches 0 at 95%
    // of the throw) — at 90% the media blinked out of a card still on screen.
    if (tp >= 0.97) return 'gone';
    // Live only while this card is THE card on top: its copy essentially
    // opaque (depth < 0.1) and not yet far into its throw (a quarter of it
    // moves the card under ~60px) — so the one prominent CTA is the live one.
    if (d < 0.1) {
      // Live once the card is visibly at rest — the ease crawls its last
      // pixel, and a CTA that looks landed must take the click.
      if (rise() < 0.995) return 'warm';
      return tp < 0.25 ? 'live' : 'leaving';
    }
    // The card above is past half its throw: this one is half in sight.
    if (d <= 0.5) return 'revealing';
    if (d <= 1.6) return 'warm';
    return 'idle';
  };
  const [phase, setPhase] = useState<MediaPhase>(phaseNow);
  useMotionValueEvent(scroll.s, 'change', () => {
    // About two cards ahead of the front (one viewport before a cluster's
    // deck rises): fetch the chunk, so mounting never waits on the network.
    if (scroll.s.get() >= clusterStart - RISE_SPAN - 1 && depth() <= 2.6) {
      preloadFeatureMedia(feature.id);
    }
    const next = phaseNow();
    setPhase((prev) => (prev === next ? prev : next));
    if (CARDS_WITH_KA.has(feature.id)) setKaInCard(feature.id, kaShownHere());
  });
  // This card draws Ka himself (the oral exam): from the moment the card
  // above has moved off him until this one has gone, the floating Ka steps
  // out.
  const kaShownHere = () => {
    const s = scroll.s.get();
    if (reduceMotion) return s >= throwStart && s < reducedEnd;
    // The card above has moved far enough off to show him (a quarter-way
    // throw uncovers the middle of the card).
    return rise() > 0 && depth() < 0.75 && throwP() < 0.97;
  };
  useEffect(() => {
    if (!CARDS_WITH_KA.has(feature.id)) return;
    setKaInCard(feature.id, kaShownHere());
    return () => setKaInCard(feature.id, false);
    // Mount / unmount only; scroll changes report above.
  }, [feature.id]);

  const x = useTransform(() => {
    scroll.layoutTick.get();
    if (reduceMotion) return 0;
    // Every card leaves to the RIGHT (Figma 82:24524).
    return thrown() * 0.8 * Math.max(window.innerWidth, 1128);
  });
  const y = useTransform(() => {
    scroll.layoutTick.get();
    if (reduceMotion) return 0;
    const riseY = (1 - rise()) * window.innerHeight * 0.95;
    const e = thrown();
    // Slight lift as the throw starts, dropping away as it leaves.
    const throwY = -70 * Math.min(1, e * 2) + 240 * e * e;
    // The pile is STRAIGHT (Figma 55:21849): every card below the top one
    // sits 8px lower and 16px narrower, so the under-cards peek out along the
    // bottom edge — no tilt.
    return riseY + 8 * Math.min(depth(), 4) + throwY;
  });
  const scale = useTransform(() =>
    // Figma 82:24524: 998 → 983.2 → 968.4 → …, 14.8px narrower per level.
    reduceMotion ? 1 : 1 - (14.8 / 998) * Math.min(depth(), 4),
  );
  // Only the throw rotates, and gently — the card leaves with a little spin
  // but never rests at an angle.
  const rotate = useTransform(() => (reduceMotion ? 0 : 8 * thrown()));
  const opacity = useTransform(() => {
    const s = scroll.s.get();
    if (reduceMotion) return s >= throwStart && s < reducedEnd ? 1 : 0;
    const riseGate = clamp01(rise() * 3);
    const depthGate = 1 - clamp01((depth() - 2.5) / 1.2);
    const throwGate = 1 - clamp01((throwP() - 0.75) / 0.2);
    return riseGate * depthGate * throwGate;
  });
  // The glass blur only where it can be seen: the top card and the one being
  // thrown. An under-card sits behind the top card's own blur, which already
  // frosts everything beneath it; blurring it too stacked a full-screen
  // backdrop pass per card for no visible change. A hidden card needs none.
  const glass = useTransform(() =>
    opacity.get() > 0.01 && depth() < 0.95 ? 'blur(10px)' : 'none',
  );
  // An under-card scales as the pile advances; without the hint every scale
  // step re-rasterised it. The front card drops the hint so it re-rasters
  // once at scale 1 and reads crisp.
  const willChange = useTransform(() => (depth() > 0.001 ? 'transform' : 'auto'));
  // Only the top card shows its copy — under-cards would ghost through the
  // translucent surfaces. Fades in continuously as the card surfaces.
  const contentOpacity = useTransform(() =>
    reduceMotion ? 1 : 1 - clamp01(depth() * 1.5),
  );

  return (
    <motion.article
      // Static stacking: card 0 on top of its pile; a mid-throw card stays
      // above the ones it reveals.
      style={{
        x,
        y,
        scale,
        rotate,
        opacity,
        zIndex: 100 - globalIndex,
        backdropFilter: glass,
        WebkitBackdropFilter: glass,
        willChange,
      }}
      className={cn(
        // Figma 82:23781: the card fills the deck's box (see DECK_BOX_CLASS);
        // 24 / 40 / 24 padding, 24 between copy and well. `will-change`: the
        // under-cards scale as the pile advances; without the hint every
        // scale step re-rasterised them.
        'absolute inset-0 flex items-center gap-6 overflow-clip rounded-[60px] py-6 pl-10 pr-6',
        GLASS_PILL_CLASS,
      )}>
      <motion.div
        style={{ opacity: contentOpacity }}
        // Figma 82:23782: the copy takes what the 565px well leaves.
        className="flex min-w-0 flex-1 flex-col items-start gap-[min(12px,1.5vh)]">
        <span
          className={cn(
            'inline-flex h-9 w-fit items-center rounded-[12px] border px-2 text-[14px] font-bold leading-[18px]',
            feature.badgeClass,
          )}>
          {t(feature.badgeKey)}
        </span>
        {/* The title and subtitle scale with the window: a narrow one narrows
            the copy column, a short one shortens the card (the window less
            292px), and badge + copy + CTA must stay inside the card either way.
            44 / 20px from about 1300 × 790 up. */}
        <h2 className="text-[length:min(44px,3.4vw,5vh)] font-bold leading-[1.18] text-secondary-800">
          {t(feature.titleKey)}
        </h2>
        <p className="text-[length:min(20px,1.6vw,2.5vh)] font-semibold leading-[1.4] text-fg-subtle">
          {t(feature.subtitleKey)}
        </p>
        {/* Only the front card at rest takes the pointer (the deck itself is
            pointer-events-none so the stage scrolls); every other card's
            button is inert, so Tab never lands on one hidden under the pile. */}
        <div
          inert={phase !== 'live'}
          className={cn(
            'mt-[min(12px,1.2vh)]',
            phase === 'live' ? 'pointer-events-auto' : 'pointer-events-none',
          )}>
          <TryItNowCta featureId={feature.id} />
        </div>
      </motion.div>
      {/* Media slot: the feature's REAL component (media/), mounted by deck
          position. Fades with the copy so an under-card's media never ghosts
          through the glass above it; only the live card takes the pointer —
          the deck itself is pointer-events-none so the stage scrolls. */}
      <motion.div
        style={{ opacity: contentOpacity }}
        // A warm or leaving card's media is on stage but not the reader's:
        // inert, so Tab never lands in a card that is hidden or moving.
        inert={phase !== 'live'}
        className={cn(
          // Figma 82:23786: the well is 565 wide with a hairline, and its
          // background IS the page's — the DS dotted pattern on `background`.
          'relative h-full w-[min(565px,61%)] shrink-0 overflow-clip rounded-[40px] border border-secondary-200 bg-background bg-dotted-pattern bg-dotted-size dark:bg-dotted-pattern-dark',
          phase === 'live' ? 'pointer-events-auto' : 'pointer-events-none',
        )}>
        <FeatureMedia featureId={feature.id} phase={phase} still={TOUCH_SCREEN} />
      </motion.div>
    </motion.article>
  );
}

// ── Store rating pills (Figma node 15:1574) ─────────────────────────────────

const STORE_RATINGS = [
  {
    id: 'ios',
    labelKey: 'landingV3.stores.ios',
    icon: '/assets/landing/app-store.png',
    score: '4.8',
  },
  {
    id: 'android',
    labelKey: 'landingV3.stores.android',
    icon: '/assets/landing/play-store.png',
    score: '4.7',
  },
] as const;

/** The two app-store rating chips from the closing section: store glyph +
 *  score + star, on the same glass pill as the rest of the page chrome. */
export function StoreRatingPills() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      {STORE_RATINGS.map((store) => (
        <div
          key={store.id}
          className={cn(
            'flex items-center gap-2 rounded-2xl py-2 pl-2 pr-3',
            GLASS_PILL_CLASS,
          )}>
          <img
            src={store.icon}
            alt={t(store.labelKey)}
            className="h-6 w-6 shrink-0 rounded-[5px] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.2)]"
          />
          <p className="whitespace-nowrap text-center text-secondary-600">
            <span className="text-[20px] font-semibold leading-7">{store.score}</span>
            {/* The star is a glyph, not copy — same in every language. */}
            <span aria-hidden className="text-[16px] font-medium leading-6">
              {'★'}
            </span>
          </p>
        </div>
      ))}
    </div>
  );
}

// ── Trusted pill (Figma node 2:11496) ───────────────────────────────────────

/**
 * Placeholder identities for the trusted-by stack — no committed photos; the
 * shared DS Avatar renders its initials fallback with its deterministic
 * per-name deck-palette colour, so the stack reads varied without shipping
 * assets. Not real people.
 */
const TRUSTED_NAMES = [
  'Sofia Rossi',
  'Liam Novak',
  'Maya Chen',
  'Noah Fischer',
  'Aisha Khan',
  'Lucas Meyer',
  'Emma Costa',
  'Jonas Weber',
  'Zoe Laurent',
  'Marco Ricci',
  'Lena Kowalski',
  'David Berg',
] as const;

/** One avatar slot swaps every 5s (0.7s crossfade) — the same ambient,
 *  no-attention-demanded cadence as the idle badge cascade and chip float. */
const AVATAR_SWAP_MS = 5000;

/**
 * `users`: the live registered-user total, or `null` while the route has not
 * answered. The pill names an EXACT number, so a `null` renders the compact
 * fallback with a trailing plus ("2.0M+") — a precise figure is only ever
 * shown for a count that actually came back.
 */
export function TrustedPill({
  users,
  copy,
}: {
  users: number | null;
  /** The words around the count; "Trusted by … students" by default. */
  copy?: { prefix: string; suffix: string };
}) {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const total =
    users === null
      ? `${formatCompactMillions(FALLBACK_USER_COUNT, i18n.language)}+`
      : formatExactCount(users, i18n.language);
  // Shuffle once per mount so the stack looks random but stays stable while
  // you watch a single rotation.
  const [pool] = useState(() => [...TRUSTED_NAMES].sort(() => Math.random() - 0.5));
  const [slots, setSlots] = useState([0, 1, 2]);
  const counterRef = useRef(3);

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setSlots((prev) => {
        const next = [...prev];
        const tick = counterRef.current;
        // Round-robin: each tick advances ONE slot to the next pool entry.
        // Slots always hold three consecutive ring positions → never collide.
        next[tick % 3] = tick % pool.length;
        counterRef.current += 1;
        return next;
      });
    }, AVATAR_SWAP_MS);
    return () => window.clearInterval(id);
  }, [pool.length, reduceMotion]);

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-[14px] py-1 pl-1 pr-3',
        GLASS_PILL_CLASS,
      )}>
      <div className="flex items-center">
        {slots.map((poolIndex, slot) => (
          <div
            key={slot}
            className={cn(
              'relative flex h-6 w-6 items-center justify-center rounded-full border border-secondary-200 bg-white/70 dark:bg-white/10',
              slot < 2 && '-mr-3',
            )}>
            <AnimatePresence initial={false}>
              <motion.span
                key={poolIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: 'easeInOut' }}
                className="absolute inset-0 flex items-center justify-center">
                <Avatar size="2xs" name={pool[poolIndex]} />
              </motion.span>
            </AnimatePresence>
          </div>
        ))}
      </div>
      <p className="text-[14px] font-medium leading-5 text-secondary-600">
        {copy?.prefix ?? t('landingV3.trusted.prefix')}{' '}
        <span className="font-bold">{t('landingV3.trusted.count', { total })}</span>{' '}
        <span className="font-bold">{copy?.suffix ?? t('landingV3.trusted.suffix')}</span>
      </p>
    </div>
  );
}

// Memoised: their props (the scroll plumbing, reduceMotion) are stable, so a
// page-level state change (the Ka stage flipping at card 0, a chip hover, the
// nudge) no longer re-renders ten cards, their media and the rail.
export const FeatureCardDeck = memo(FeatureCardDeckInner);
export const FeatureRail = memo(FeatureRailInner);
